import { zodResolver } from "@hookform/resolvers/zod";
import { CameraAltSharp, CardMembershipSharp } from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useSnackbar } from "../contexts/SnackbarContext";
import { uploadImage, viewImage } from "../services/upload.service";
import { updateInfo } from "../services/user.service";
import { useAuthStore } from "../stores/store";
import { AuthenStoreType } from "../types/authen";
import { InfoForm } from "../types/user";
import ChangePasswordForm from "./ChangePasswordForm";

type PropsType = {
  mutate: any;
  formData: any;
};

const ProfileSettingForm = (props: PropsType) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { user: auth, setAuthState: setAuth } = useAuthStore(
    (state) => state as AuthenStoreType,
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);

  const formSchema = z.object({
    email: z
      .string()
      .min(1, {
        message: "Email is required. Please enter an email.",
      })
      .email({
        message: "Please enter a valid email address.",
      }),
    name: z.string().min(1, {
      message: "Full Name is required. Please enter a full name.",
    }),
    mobile_no: z.string().min(1, {
      message: "Mobile is required. Please enter a mobile.",
    }),
    image: z.string().optional(),
  });

  const { control, reset, handleSubmit, setValue } = useForm<
    z.infer<typeof formSchema>
  >({
    resolver: zodResolver(formSchema),
    defaultValues: props?.formData,
  });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file: File): Promise<object> => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await uploadImage(formData);
      if (response) {
        setValue("image", response?.fileName);
        const fileUrl = viewImage(response?.fileName);
        if (fileUrl) {
          setProfileImage(fileUrl);
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      showSnackbar("Invalid File", "error");
    } finally {
      // setIsLoading(false);
    }

    return {};
  };

  useEffect(() => {
    reset(props?.formData);
    if (props?.formData?.image) {
      const fileUrl = viewImage(props?.formData?.image);
      if (fileUrl) {
        setProfileImage(fileUrl);
      }
    }
  }, [props?.formData, reset]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const info: InfoForm = {
      name: values.name,
      mobile_no: values.mobile_no,
      image: values.image,
    };

    setIsLoading(true);
    try {
      let response = await updateInfo(info);

      if (response && response?.id) {
        if (auth) {
          let userAuth = auth;
          userAuth.name = response?.name;
          userAuth.mobile_no = response?.mobile_no;
          userAuth.image = response?.image;
          setAuth(userAuth);
        }
        props.mutate();
        showSnackbar("Changes Saved Successfully!", "success");
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Changes Saved Failed", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Typography variant="subtitle1" className="font-bold mb-4">
        Profile Settings
      </Typography>
      <Card className="mb-4 p-4">
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="body1" className="font-bold mb-4">
            User Profile
          </Typography>
          <div className="container grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="col-span-full flex flex-col gap-3">
              <InputLabel htmlFor="image">Profile Picture</InputLabel>
              <Stack
                direction={"row"}
                useFlexGap
                spacing={2}
                alignItems={"center"}
                onClick={() => fileInputRef.current?.click()}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    <Box
                      sx={{
                        borderRadius: "50%",
                        backgroundColor: "#ffffff",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                      }}
                    >
                      <CameraAltSharp sx={{ width: 16, height: 16 }} />
                    </Box>
                  }
                >
                  <Avatar
                    alt="profile-image"
                    sx={{ width: 64, height: 64 }}
                    src={profileImage ?? undefined}
                  />
                </Badge>
                <div>
                  <Typography variant="body2">Upload Profile</Typography>
                  <Typography variant="body2" color="grey.400">
                    Allowed file types: png, jpg, jpeg
                  </Typography>
                </div>
              </Stack>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }} // Hide the file input
                onChange={handleImageChange}
              />
            </div>
            <div className="col-span-1 flex flex-col gap-3">
              <InputLabel htmlFor="email">Email</InputLabel>
              <Controller
                name="email"
                control={control}
                render={({
                  field: { ref, onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    placeholder="Email"
                    fullWidth
                    value={value}
                    inputRef={ref}
                    disabled
                  />
                )}
              />
            </div>
            <div className="col-span-1 md:flex md:flex-col md:gap-3 hidden"></div>
            <div className="col-span-1 flex flex-col gap-3">
              <InputLabel htmlFor="name" required>
                Full Name
              </InputLabel>
              <Controller
                name="name"
                control={control}
                render={({
                  field: { ref, onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    placeholder="Full Name"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    onChange={onChange}
                    value={value}
                    inputRef={ref}
                  />
                )}
              />
            </div>
            <div className="col-span-1 flex flex-col gap-3">
              <InputLabel htmlFor="mobile_no" required>
                Mobile
              </InputLabel>
              <Controller
                name="mobile_no"
                control={control}
                render={({
                  field: { ref, onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    placeholder="Mobile"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    onChange={onChange}
                    value={value}
                    inputRef={ref}
                  />
                )}
              />
            </div>
            <div className="col-span-1 flex flex-col gap-3">
              <InputLabel htmlFor="role">User Role</InputLabel>
              <Stack
                direction={"row"}
                useFlexGap
                spacing={2}
                alignItems={"center"}
                p={2}
                className="bg-gray-100 rounded"
              >
                <CardMembershipSharp className="w-12 h-12 text-primary" />
                {auth?.role === "admin" ? (
                  <div>
                    <Typography
                      variant="body2"
                      className="font-bold text-primary"
                    >
                      Admin
                    </Typography>
                    <Typography variant="body2" className="text-primary">
                      Best full access for Setting Management
                    </Typography>
                  </div>
                ) : (
                  <div>
                    <Typography
                      variant="body2"
                      className="font-bold text-primary"
                    >
                      User
                    </Typography>
                    <Typography variant="body2" className="text-primary">
                      Best for Access Working
                    </Typography>
                  </div>
                )}
              </Stack>
            </div>
            <div className="col-span-1 md:flex-col gap-3 md:flex hidden"></div>
          </div>
          <Stack
            direction={"row"}
            justifyContent={"end"}
            useFlexGap
            spacing={2}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ width: "120px" }}
            >
              Discard
            </Button>
            <Button
              variant="outlined"
              color="red"
              onClick={() => setOpenDialog(true)}
            >
              Change Password
            </Button>
            <Button
              type="submit"
              variant="contained"
              loading={isLoading}
              sx={{ width: "120px" }}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Card>
      <ChangePasswordForm open={openDialog} setOpen={setOpenDialog} />
    </>
  );
};

export default ProfileSettingForm;
